import { Factory, Package, Sprout, Truck, Warehouse } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useGetFacilityWholeList } from "../../../../../api/cultivations";
import { FacilityType } from "../../../../../config/enums/FacilityType.enum";
import {
  ApiFarmGate,
  ApiProcessGate,
  CultivationRecord,
  InternalStorageRecord,
  InternalTransportRecord
} from "../../../../../types/prodcut-data-review/product-data.types";
import InternalTransportTable from "../../transportRecords/components/InternalTransportTable";
import PackagingRecordsList from "../../packagingRecords/PackagingRecordsList";
import CultivationEditor from "../../cultivationEditor/CultivationEditor";
import ProcessRecordsList from "../../processRecords/ProcessRecordsList";
import StorageTable from "../../storageRecords/StorageTable";
import { GateKind } from "../types/gate.types";
import { toStorageRecord, toTransportRecord } from "../utils/gateCardContent.utils";
import Subsection from "./Subsection";

export interface GateCardContentProps {
  kind: GateKind;
  gate: ApiFarmGate | ApiProcessGate;
  onChange: (next: ApiFarmGate | ApiProcessGate) => void;
  ownerId: string;
  sourceId: string;
  refetchReviewData?: () => void;
  disabled?: boolean;
  transportTypeMap: Record<number, string>;
}

const GateCardContent: React.FC<GateCardContentProps> = ({
  kind,
  gate,
  onChange,
  ownerId,
  sourceId,
  refetchReviewData,
  disabled = false,
  transportTypeMap
}) => {
  const { data: facilities } = useGetFacilityWholeList(FacilityType.STORAGE);
  const facilityMap = useMemo<Record<string, string>>(
    () => Object.fromEntries((facilities ?? []).map((f: any) => [f.value, f.text])),
    [facilities]
  );

  const [storageRecords, setStorageRecords] = useState<InternalStorageRecord[]>(() =>
    (gate.internalStorage ?? []).map(api => toStorageRecord(api, facilityMap))
  );
  const [transportRecords, setTransportRecords] = useState<InternalTransportRecord[]>(() =>
    ((gate as ApiFarmGate).internalTransportation ?? []).map(api => toTransportRecord(api, transportTypeMap))
  );

  useEffect(() => {
    setStorageRecords((gate.internalStorage ?? []).map(api => toStorageRecord(api, facilityMap)));
  }, [gate.internalStorage, facilityMap]);

  useEffect(() => {
    const internalTransportation = (gate as ApiFarmGate).internalTransportation ?? [];
    setTransportRecords(internalTransportation.map(api => toTransportRecord(api, transportTypeMap)));
  }, [gate, transportTypeMap]);

  const itOps = {
    save: (id: string, patch: Partial<InternalTransportRecord>) =>
      setTransportRecords(prev => prev.map(r => (r.id === id ? { ...r, ...patch } : r)))
  };

  const isOps = {
    save: (id: string, patch: Partial<InternalStorageRecord>) =>
      setStorageRecords(prev => prev.map(r => (r.id === id ? { ...r, ...patch } : r)))
  };

  const isFarm = kind === "farm";
  const farmGate = gate as ApiFarmGate;
  const processGate = gate as ApiProcessGate;

  const update = (patch: Partial<ApiFarmGate & ApiProcessGate>) =>
    onChange({ ...gate, ...patch } as any);

  const cultivationData: CultivationRecord | undefined = useMemo(() => {
    if (isFarm && farmGate) {
      return {
        id: farmGate._id ?? "",
        cropName: farmGate.produceCultivar ?? "",
        fieldName: farmGate.farmingField ?? "",
        dataType: farmGate.dataType ?? "",
        produceType: farmGate.produceType ?? "",
        originCountry: farmGate.originCountry ?? "",
        harvestYear: farmGate.cultivationField?.harvestYear ?? "",
        area: farmGate.cultivationField?.growthArea ?? "",
        yieldAmount: farmGate.cultivationField?.yield ?? "",
        yieldUnit: "kg",
        notes: "",
        cultivationFields: farmGate.cultivationField ?? undefined
      };
    }
  }, [farmGate, isFarm]);

  return (
    <>
      {isFarm && farmGate.cultivationField && (
        <Subsection title="Cultivation" icon={Sprout} count={1}>
          <CultivationEditor
            rec={cultivationData}
            disabled={disabled}
            onChange={patch =>
              update({
                produceCultivar: patch.cropName ?? farmGate.produceCultivar,
                farmingField: patch.fieldName ?? farmGate.farmingField,
                dataType: patch.dataType !== undefined ? patch.dataType : farmGate.dataType,
                produceType: patch.produceType !== undefined ? patch.produceType : farmGate.produceType,
                originCountry:
                  patch.originCountry !== undefined ? patch.originCountry : farmGate.originCountry,
                cultivationField: {
                  ...farmGate.cultivationField,
                  ...patch.cultivationFields,
                  harvestYear:
                    patch.harvestYear !== undefined
                      ? patch.harvestYear
                      : farmGate.cultivationField?.harvestYear,
                  growthArea: patch.area !== undefined ? patch.area : farmGate.cultivationField?.growthArea,
                  yield: patch.yieldAmount !== undefined ? patch.yieldAmount : farmGate.cultivationField?.yield
                }
              } as any)
            }
            onSaved={refetchReviewData}
          />
        </Subsection>
      )}

      <Subsection title="Processes" icon={Factory} count={processGate.processing?.length ?? 0}>
        <ProcessRecordsList
          processes={processGate.processing ?? []}
          processGateId={processGate._id}
          gateKind={kind}
          ownerId={ownerId}
          sourceId={sourceId}
          onUpdate={next => update({ processing: next } as any)}
          disabled={disabled}
        />
      </Subsection>

      <Subsection title="Packaging" icon={Package} count={gate.packaging?.length ?? 0}>
        <PackagingRecordsList
          packagings={gate.packaging ?? []}
          gateId={gate._id}
          gateKind={kind}
          disabled={disabled}
        />
      </Subsection>

      <Subsection title="Internal Storage" icon={Warehouse} count={storageRecords.length}>
        <StorageTable
          label="internal storages"
          records={storageRecords}
          onSave={isOps.save}
          disabled={disabled}
        />
      </Subsection>

      <Subsection title="Internal Transport" icon={Truck} count={transportRecords.length}>
        <InternalTransportTable records={transportRecords} onSave={itOps.save} disabled={disabled} />
      </Subsection>
    </>
  );
};

export default GateCardContent;
